/*
 * =============================================================================
 * FILE HEADER — packages/sdk/src/ir-to-sdk.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why             | What Changed                  |
 * |---|------------------------|-------------------|-----------------|-------------------------------|
 * | 1 | 2026-06-17 (Phase 1)   | Claude Sonnet 4.6 | SDK Step        | irToSdkSource() code generator|
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - packages/sdk/src/index.ts           (re-exported)
 * - apps/studio/src/editor/CodeView.ts  (read-only Monaco TypeScript view)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * irToSdkSource(ir) → string  — generates TypeScript source from a TestIR (IR → TS, one-way)
 *
 * CALLER → CALLEE MAP
 * --------------------
 * Studio CodeView → irToSdkSource(ir) → Monaco read-only display
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. This is a one-way code generator (IR → TypeScript). Round-trip TS→IR decompilation
 *    is explicitly out of scope — that is a tarpit.
 * 2. Generated code is human-readable but not necessarily idiomatic. Its purpose is to
 *    show "what the recorder captured" in a format SDETs can copy and refine.
 * 3. Selector code generation picks the most specific factory per candidate:
 *    a11yId → byId, text → byText, textContains → byTextContains, etc.
 *    Multiple candidates are joined with .or() in order (first = highest priority).
 * 4. Container steps (if/repeat/forEachRow) are not yet generated (Phase 2). They
 *    emit a TODO comment so the output is still valid TypeScript.
 * 5. Output always starts with an import from "@mimiq/sdk" that only imports what is used.
 *
 * =============================================================================
 */
// ── Selector code generation ─────────────────────────────────────────────────
function candidateToCall(c) {
    switch (c.strategy) {
        case "a11yId": return `byId(${q(c.value)})`;
        case "text": return `byText(${q(c.value)})`;
        case "textContains": return `byTextContains(${q(c.value)})`;
        case "contentDesc": return `byContentDesc(${q(c.value)})`;
        case "className": return `byClass(${q(c.value)})`;
        case "xpath": return `byXPath(${q(c.value)})`;
        case "css": return `byCss(${q(c.value)})`;
        default: return `byId(${q(c.value)})`;
    }
}
function selectorToCode(sel) {
    if (sel.candidates.length === 0) {
        return `byId("").withDescription(${q(sel.description)})`;
    }
    const parts = sel.candidates.map((c) => candidateToCall(c));
    // Chain with .or()  →  byId('x').or(byText('y'))
    let code = parts[0];
    for (let i = 1; i < parts.length; i++) {
        code += `.or(${parts[i]})`;
    }
    // Add .withDescription() only if description differs from the first candidate's value
    if (sel.description && sel.description !== sel.candidates[0].value) {
        code += `.withDescription(${q(sel.description)})`;
    }
    return code;
}
// ── Step code generation ─────────────────────────────────────────────────────
function stepToCode(step, indent) {
    const i = indent;
    switch (step.type) {
        case "launchApp":
            return `${i}f.launchApp(${q(step.packageName)}${step.clearState ? ", { clearState: true }" : ""});`;
        case "stopApp":
            return `${i}f.stopApp(${q(step.packageName)});`;
        case "openUrl":
            return `${i}f.openUrl(${q(step.url)});`;
        case "deepLink":
            return `${i}f.deepLink(${q(step.url)}${step.packageName ? `, ${q(step.packageName)}` : ""});`;
        case "tap":
            return `${i}f.tap(${selectorToCode(step.selector)});`;
        case "doubleTap":
            return `${i}f.doubleTap(${selectorToCode(step.selector)});`;
        case "longPress":
            return `${i}f.longPress(${selectorToCode(step.selector)}${step.durationMs !== 800 ? `, ${step.durationMs}` : ""});`;
        case "typeText": {
            const hasOpts = step.selector || step.sensitive;
            if (!hasOpts)
                return `${i}f.typeText(${q(step.text)});`;
            const optsArr = [];
            if (step.selector)
                optsArr.push(`selector: ${selectorToCode(step.selector)}`);
            if (step.sensitive)
                optsArr.push("sensitive: true");
            return `${i}f.typeText(${q(step.text)}, { ${optsArr.join(", ")} });`;
        }
        case "clearText":
            return `${i}f.clearText(${selectorToCode(step.selector)});`;
        case "pressKey":
            return `${i}f.pressKey(${q(step.key)});`;
        case "hideKeyboard":
            return `${i}f.hideKeyboard();`;
        case "swipe":
            return `${i}f.swipe([${step.from.join(", ")}], [${step.to.join(", ")}]${step.durationMs !== 300 ? `, ${step.durationMs}` : ""});`;
        case "scrollUntilVisible": {
            const hasOpts = step.direction !== "down" || step.maxScrolls !== 10 || step.scrollable;
            if (!hasOpts)
                return `${i}f.scrollUntilVisible(${selectorToCode(step.selector)});`;
            const optsArr = [];
            if (step.direction !== "down")
                optsArr.push(`direction: ${q(step.direction)}`);
            if (step.maxScrolls !== 10)
                optsArr.push(`maxScrolls: ${step.maxScrolls}`);
            if (step.scrollable)
                optsArr.push(`scrollable: ${selectorToCode(step.scrollable)}`);
            return `${i}f.scrollUntilVisible(${selectorToCode(step.selector)}, { ${optsArr.join(", ")} });`;
        }
        case "waitFor": {
            const hasOpts = step.state !== "visible" || step.timeoutMs !== undefined;
            if (!hasOpts)
                return `${i}f.waitFor(${selectorToCode(step.selector)});`;
            const optsArr = [];
            if (step.state !== "visible")
                optsArr.push(`state: ${q(step.state)}`);
            if (step.timeoutMs !== undefined)
                optsArr.push(`timeoutMs: ${step.timeoutMs}`);
            return `${i}f.waitFor(${selectorToCode(step.selector)}, { ${optsArr.join(", ")} });`;
        }
        case "wait":
            return `${i}f.wait(${step.durationMs});`;
        case "assert":
            return `${i}f.assert(${selectorToCode(step.selector)}, ${q(step.assertion)}${step.value !== undefined ? `, ${q(step.value)}` : ""});`;
        case "screenshot":
            return `${i}f.screenshot(${step.label ? q(step.label) : ""});`;
        case "setDeviceState": {
            const parts = [];
            if (step.wifi !== undefined)
                parts.push(`wifi: ${step.wifi}`);
            if (step.airplane !== undefined)
                parts.push(`airplane: ${step.airplane}`);
            return `${i}// setDeviceState({ ${parts.join(", ")} }) — not yet in SDK builder`;
        }
        case "if":
        case "repeat":
        case "forEachRow":
        case "callFlow":
        case "api":
        case "script":
            return `${i}// TODO: ${step.type} step (use IR JSON for complex steps)`;
        default:
            return `${i}// Unknown step type`;
    }
}
// ── Imports collector ────────────────────────────────────────────────────────
function collectImports(ir) {
    const used = new Set(["defineTest"]);
    const strategies = new Set();
    function walkSteps(steps) {
        for (const step of steps) {
            if ("selector" in step && step.selector) {
                for (const c of step.selector.candidates)
                    strategies.add(c.strategy);
            }
            if (step.type === "if") {
                walkSteps(step.then);
                if (step.else)
                    walkSteps(step.else);
            }
            if (step.type === "repeat")
                walkSteps(step.steps);
            if (step.type === "forEachRow")
                walkSteps(step.steps);
        }
    }
    for (const flow of ir.flows)
        walkSteps(flow.steps);
    walkSteps(ir.hooks.beforeEach);
    walkSteps(ir.hooks.afterEach);
    if (strategies.has("a11yId"))
        used.add("byId");
    if (strategies.has("text"))
        used.add("byText");
    if (strategies.has("textContains"))
        used.add("byTextContains");
    if (strategies.has("contentDesc"))
        used.add("byContentDesc");
    if (strategies.has("className"))
        used.add("byClass");
    if (strategies.has("xpath"))
        used.add("byXPath");
    if (strategies.has("css"))
        used.add("byCss");
    // Check for env references
    const irStr = JSON.stringify(ir);
    if (irStr.includes("${env:"))
        used.add("env");
    return [...used].sort();
}
// ── Main export ──────────────────────────────────────────────────────────────
/**
 * Generate a TypeScript source file that represents the given TestIR.
 * Used by Studio's Monaco read-only code view. Output is human-readable but one-way only.
 */
export function irToSdkSource(ir) {
    const imports = collectImports(ir);
    const lines = [];
    lines.push(`import { ${imports.join(", ")} } from "@mimiq/sdk";`);
    lines.push("");
    // Build targets string
    const targets = ir.config.targets.map((t) => q(t)).join(", ");
    // Build app config
    const appParts = [];
    if (ir.config.app?.packageName)
        appParts.push(`packageName: ${q(ir.config.app.packageName)}`);
    if (ir.config.app?.bundleId)
        appParts.push(`bundleId: ${q(ir.config.app.bundleId)}`);
    if (ir.config.app?.url)
        appParts.push(`url: ${q(ir.config.app.url)}`);
    const optsLines = [];
    optsLines.push(`  targets: [${targets}],`);
    if (appParts.length > 0)
        optsLines.push(`  app: { ${appParts.join(", ")} },`);
    const envKeys = Object.keys(ir.config.env ?? {});
    if (envKeys.length > 0) {
        const envPairs = envKeys.map((k) => `${k}: ${q(ir.config.env[k])}`).join(", ");
        optsLines.push(`  env: { ${envPairs} },`);
    }
    if (ir.meta.tags && ir.meta.tags.length > 0) {
        optsLines.push(`  tags: [${ir.meta.tags.map(q).join(", ")}],`);
    }
    lines.push(`export default defineTest(${q(ir.meta.name)}, {`);
    for (const l of optsLines)
        lines.push(l);
    lines.push(`}, (t) => {`);
    for (const flow of ir.flows) {
        lines.push(`  t.flow(${q(flow.name)}, (f) => {`);
        for (const step of flow.steps) {
            lines.push(stepToCode(step, "    "));
        }
        lines.push(`  });`);
    }
    lines.push(`});`);
    return lines.join("\n");
}
// ── Helpers ───────────────────────────────────────────────────────────────────
/** JSON-stringify a string value (adds surrounding quotes + escapes). */
function q(s) {
    return JSON.stringify(s);
}
//# sourceMappingURL=ir-to-sdk.js.map